import { encode } from "base-64";
import CryptoJS from "crypto-js";
import jwt from "jsonwebtoken";
import { ENCRYPTION_KEY } from "..";
import {
  User,
  setData as setUserData,
  setResetData as setUserDataReset,
  setNeedCallAdditionalData,
  setResetPersistData,
  userType as userTypeData,
  IUserData,
} from "@src/@slice/userSlice";
import {
  ISignUpData,
  setData as setSignUpData,
  setResetData as setSignUpDataReset,
} from "@src/@slice/signUpSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  login,
  exchangeLoginToken,
  logout,
  submitUserInfo,
  getUserAdditionalInfo,
  changeNickname,
  changePassword,
  changeProfileImg,
  changeAdditionalInfo,
  changeWeddingInfo,
} from "@src/api/sign/sign";
import { TAdditionalPageType } from "@src/components/signInUp/common/useSignUpReducer";
import { APP_START_PATH_KEY } from "./useInitUser";
import { waitFor } from "@src/shared/src";
import { postRefreshToken } from "@src/api/common";

let needCallAdditionalData = true;

export const useUserActions = () => {
  const dispatch = useDispatch();
  const user = useSelector(User);
  const userType = useSelector(userTypeData);
  // 로그인 요청 함수
  const loginAction = async (email: string, password: string): Promise<any> => {
    const encodedPassword = encode(password);
    const data = {
      loginId: email,
      pwd: encodedPassword,
    };
    const result = await new Promise<any>((resolve, reject) => {
      login(data)
        .then((response) => {
          if (response.data.result === 1) {
            resolve(getUserData(response.data.item.accessToken, response.data.item.refreshToken));
          }
          resolve(response.data);
        })
        .catch((e) => {
          console.log(e);
          reject(e);
        });
    });
    return result;
  };

  // 소셜 로그인 요청 함수
  const socialLoginAction = async (token: string): Promise<any> => {
    const result = await new Promise<any>((resolve, reject) => {
      exchangeLoginToken(token)
        .then((response) => {
          if (response.data.result === 1) {
            resolve(getUserData(response.data.item.accessToken, response.data.item.refreshToken));
          } else {
            resolve(response.data);
          }
        })
        .catch((e) => {
          console.log(e);
          reject(e);
        });
    });
    return result;
  };

  // 사용자 정보 가져오기
  const getUserData = (accessToken?: string, refreshToken?: string): IUserData => {
    const token = accessToken || localStorage.getItem("accessToken");
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    }
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }

    // AES 복호화
    const decryptAES = (encryptedData: string, key: string) => {
      const keyHex = CryptoJS.enc.Utf8.parse(key);
      const decrypted = CryptoJS.AES.decrypt(encryptedData, keyHex, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
      return decrypted.toString(CryptoJS.enc.Utf8);
    };

    // JWT 디코딩
    const decode = jwt.decode(token);

    // 사용자 정보 객체 생성
    let userData: IUserData = {} as IUserData;

    // 사용자 정보 객체에 값 할당
    for (let key in decode) {
      let value: string;
      try {
        value = decryptAES(decodeURIComponent(decode[key]), ENCRYPTION_KEY);
      } catch (e) {
        console.log("error");
        value = decode[key];
      }
      userData[key] = value;
    }
    const data = {
      data: {
        personalInfo: {
          loginId: userData.loginId,
          nick: userData.nick,
          role: userData.sex,
          name: userData.name,
          tel: userData.tel,
        },
      },
    };
    dispatch(setUserData({ data: userData }));
    dispatch(setSignUpData(data));
    return userData;
  };

  // 토큰 삭제
  const removeToken = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  };

  // userdata, signupdata 삭제
  const removeUserData = () => {
    dispatch(setUserDataReset());
    dispatch(setSignUpDataReset());
  };

  // 사용자 정보 리셋
  const resetUserData = () => {
    removeToken();
    removeUserData();

    // 네이티브 앱 토큰도 삭제 (재접속 시 자동 로그인 방지)
    if (window.wb_bridge?.clearTokens) {
      window.wb_bridge.clearTokens();
    } else if (window.webkit?.messageHandlers?.clearTokens) {
      window.webkit.messageHandlers.clearTokens.postMessage("");
    }
  };

  // 로그아웃 요청 함수
  const handleConditionalLogout = async (type: "webview" | "" = "") => {
    let refreshToken = localStorage.getItem("refreshToken");
    let accessToken = localStorage.getItem("accessToken");
    if (accessToken === 'undefined' || refreshToken === 'undefined') {
      accessToken = null;
      refreshToken = null;
    }
    const socialLoginCheck = window.location.pathname === APP_START_PATH_KEY;
    if (document.visibilityState === "hidden") return false;
    let needLogout = false;
    let isDeletedAccount = false;
    if (type === "webview") {
      if (socialLoginCheck) {
        if (!accessToken || !refreshToken) {
          needLogout = true;
        } else {
          // 토큰 갱신 시도해보고 실패하면 로그아웃
          try {
            const res = await postRefreshToken(refreshToken);
            if (res.data.result === 0) {
              if (res.data.item === null && res.data.msg?.includes("데이터가 존재하지 않습니다")) {
                // 탈퇴된 계정
                isDeletedAccount = true;
                // 이 경우 최근 로그인한 항목도 제거
                localStorage.removeItem("h2mwbell:last");
              } else {
                // 이미 로그아웃된 상태인가? 여튼 갱신 실패일때
                needLogout = true;
              }
            } else if (res?.data?.item?.accessToken) {
              // 토큰 갱신 성공
              getUserData(res?.data?.item?.accessToken, res?.data?.item?.refreshToken);
            }
          } catch (error) {
            console.error("Token refresh failed", error);
            needLogout = true;
          }
        }
      } else {
        needLogout = true;
      }
      if (!needLogout && !isDeletedAccount) return false;
      if (!isDeletedAccount && accessToken && refreshToken) {
        return logout(refreshToken)
          .then((response) => {
            // 진짜 로그아웃
            resetUserData();
            dispatch(setResetPersistData());
            // 쿠키도 삭제
            // document.cookie = `h2mCookieAccessToken=; path=/; domain=.h2m.io; Secure; SameSite=None; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            // document.cookie = `h2mCookieRefreshToken=; path=/; domain=.h2m.io; Secure; SameSite=None; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            return response.data?.result === 1 ? true : false;
          })
          .catch((e) => {
            console.log(e);
            return false;
          });
      } else {
        resetUserData();
        dispatch(setResetPersistData());
        return true;
      }
    }
  };

  // 회원 가입
  const registerCustomer = async (x: Partial<ISignUpData>, joinToken?: string) => {
    const { loginId: email, password: pwd, tel, role, name: customerName } = x.personalInfo;
    const termsData = x.agreement;
    const termsArray = [];
    const encodedPwd = pwd ? encode(pwd) : null;
    const termsCondition = {
      terms: 1,
      privacyPolicy: 2,
      marketing: 3,
      applianceBenefits: 4,
      over14: 5,
    };
    Object.keys(termsCondition).forEach((key) => {
      if (termsData[key] === true) {
        termsArray.push(termsCondition[key]);
      }
    });
    const termsAgreed = termsArray.join(",");
    const data = {
      email: email,
      pwd: encodedPwd,
      tel: tel,
      sex: role,
      customerName: customerName,
      terms: termsAgreed,
    };
    if (joinToken) {
      data["joinToken"] = joinToken;
    }

    // 유효하지 않은 값 제거
    const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const result = submitUserInfo(filteredData, "signUp")
      .then((response) => {
        if (response.data.result === 0) {
          return { success: false, data: response.data };
        } else {
          getUserData(response.data.item.accessToken, response.data.item.refreshToken);
          return { success: true, data: response.data };
        }
      })
      .catch((error) => {
        console.log("error", error);
        return { success: false, data: null };
      });
    return result;
  };

  // 회원정보 수정
  const modifyCustomer = async (
    params: { [key: string]: string | number },
    type: TAdditionalPageType,
  ) => {
    // 이전 수정 코드 --> 회원 정보 수정 롤백하거나 하게되면 다시 사용할 수도 있을 것 같아 주석으로 처리해둠
    // const result = submitUserInfo(filteredData, "modify")
    //   .then((response) => {
    //     if (response.data.result === 0) {
    //       return { success: false, data: response.data };
    //     } else {
    //       getUserData(response.data.item.accessToken);
    //       dispatch(setNeedCallAdditionalData({ boo: true }));
    //       needCallAdditionalData = true;
    //       return { success: true, data: response.data };
    //     }
    //   })
    //   .catch((error) => {
    //     return { success: false, error: error.response };
    //   });
    // return result;
    return new Promise<{ success: boolean; data?: any; error?: any }>(async (resolve, reject) => {
      try {
        const result =
          type === "Signup_Complete_v2"
            ? await changeAdditionalInfo(params as any)
            : await changeWeddingInfo(params as any);
        if (result.data.item.accessToken) {
          getUserData(result.data.item.accessToken);
          dispatch(setNeedCallAdditionalData({ boo: true }));
          resolve({ success: true, data: params });
        } else {
          resolve({ success: false, data: result.data });
        }
      } catch (error) {
        console.log("error", error);
        resolve({ success: false, error: error });
      }
    });
  };

  // 추가정보 호출
  const getAdditionalInfo = async () => {
    if (user.needCallAdditionalData === false) return false;
    if (!needCallAdditionalData) return false;
    dispatch(setNeedCallAdditionalData({ boo: false }));
    needCallAdditionalData = false;
    const result = getUserAdditionalInfo()
      .then((response) => {
        const res = response.data;
        const data = {
          data: {
            pesonalInfo: {
              nick: res.item.nick,
            },
            weddingInfo: {
              ...(res.item.mateName && { mateName: res.item.mateName }),
              ...(res.item.mateTel && { mateTel: res.item.mateTel }),
              ...(res.item.weddingTsp && { weddingTsp: res.item.weddingTsp }),
              ...(res.item.weddingTspType && { weddingTspType: res.item.weddingTspType }),
              ...(res.item.region && { region: res.item.region }),
              ...(res.item.weddinghallName && { weddinghallName: res.item.weddinghallName }),
            },
          },
        };

        // 웨딩지역, 예식날짜는 따로 기본값이 있어서 명시적으로 null로 변경안해주면 초기값인지, 수정해서 바뀐 미정값인지 구분이 안가서 예식일이 null일때 null로 바꿔줌
        if (res.item.weddingTsp === null) {
          data.data.weddingInfo.region = null;
          data.data.weddingInfo.weddingTspType = null;
        }
        dispatch(setUserData({ data: res.item }));
        dispatch(setSignUpData(data));
        return response.data;
      })
      .catch((error) => {
        console.log(error);
      });
    return result;
  };

  // 닉네임 수정
  const modifyNick = async (nick: string) => {
    const result = changeNickname(nick)
      .then((response) => {
        if (response.data.result === 0) {
          return { success: false, data: response.data };
        } else {
          getUserData(response.data.item);
          return { success: true, data: response.data };
        }
      })
      .catch((error) => {
        return { success: false, error: error.response };
      });

    return result;
  };

  // 비밀번호 수정
  const modifyPassword = async (password: string, newPassword: string) => {
    const encodedPassword = encode(password);
    const encodedNewPassword = encode(newPassword);
    const result = changePassword(encodedPassword, encodedNewPassword)
      .then((response) => {
        if (response?.data?.result === 0) {
          return { success: false, data: response.data };
        } else {
          if (response?.data?.item?.accessToken) {
            getUserData(response.data.item.accessToken);
          }
          return { success: true, data: response.data };
        }
      })
      .catch((error) => {
        return { success: false, error: error.response };
      });

    return result;
  };

  // 프로필 이미지 변경
  const modifyProfileImage = async (imgUrl: string) => {
    const result = changeProfileImg(imgUrl)
      .then((response) => {
        if (response?.data?.result?.result !== "ok") {
          return { success: false, data: response.data };
        } else {
          getUserData(response.data.item);
          return { success: true, data: response.data };
        }
      })
      .catch((error) => {
        return { success: false, error: error.response };
      });

    return result;
  };

  return {
    loginAction,
    socialLoginAction,
    getUserData,
    removeToken,
    removeUserData,
    resetUserData,
    handleConditionalLogout,
    registerCustomer,
    modifyCustomer,
    getAdditionalInfo,
    modifyNick,
    modifyPassword,
    modifyProfileImage,
  };
};
